import { useEffect, useMemo, useState } from "react";

import { computeChangedFields, ExistingDestination, PreparedDestination } from "@prequel/react";

import { TestConnectionResult } from "@/api/client";
import { getErrorMessage } from "@/api/helpers";
import { useTestExistingDestination, useTestNewDestination } from "@/api/queries/destinations";
import TestConnectionCard from "@/components/TestConnectionCard";
import { ExistingDestinationPayload } from "@/types";

type TestDestinationConnectionProps = {
  key: string; // Required unique key identifier for the test connection target
  beforeSubmitTest?: () => boolean;
  preparedDestination?: PreparedDestination;
  existingDestination?: ExistingDestination | null;
  onTestStateChange?: (state: { status: string }) => void;
};
const TestDestinationConnection = ({
  beforeSubmitTest = () => true,
  preparedDestination,
  existingDestination,
  onTestStateChange,
}: TestDestinationConnectionProps) => {
  const testNew = useTestNewDestination();
  const testExisting = useTestExistingDestination();

  const isEditing = existingDestination !== undefined;
  const activeTest = isEditing ? testExisting : testNew;

  // preparedDestination is rebuilt on every render, so key on its contents rather than its identity.
  const preparedKey = JSON.stringify(preparedDestination);
  const [testedKey, setTestedKey] = useState<string>();

  const destinationTest: TestConnectionResult = useMemo(() => {
    if (activeTest.isPending) {
      return { status: "processing" };
    }
    // A result describes the connection as it was configured when the test ran, so an edit since then voids it.
    if (testedKey !== preparedKey) {
      return { status: undefined };
    }
    if (activeTest.isError) {
      return {
        status: "error",
        message: getErrorMessage(activeTest.error),
      };
    }
    return activeTest.data || { status: undefined };
  }, [activeTest.data, activeTest.isPending, activeTest.isError, activeTest.error, testedKey, preparedKey]);

  useEffect(() => {
    onTestStateChange?.({ status: destinationTest.status ?? "idle" });
  }, [destinationTest, onTestStateChange]);

  const onTestConnection = () => {
    if (!beforeSubmitTest()) {
      return;
    }

    setTestedKey(preparedKey);

    // If we have both, we're in the edit state of the DestinationForm and need to compute fields that changed
    if (isEditing && preparedDestination) {
      const payload: ExistingDestinationPayload = {
        destinationID: existingDestination!.id,
      };
      const changed = computeChangedFields(existingDestination!, preparedDestination!);
      if (Object.keys(changed).length > 0) {
        payload.fields = changed;
      }
      testExisting.mutate(payload);
    } else if (isEditing) {
      testExisting.mutate({
        destinationID: existingDestination!.id,
      });
    } else {
      testNew.mutate(preparedDestination!);
    }
  };

  return (
    <TestConnectionCard
      test={destinationTest}
      request={{ status: undefined }}
      connectionType="destination"
      usesPreparedConnection={preparedDestination !== undefined}
      onTestConnection={onTestConnection}
      className="max-w-4xl"
    />
  );
};

export default TestDestinationConnection;
